import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { reportsApi } from 'services/ReportManagement/reports'
import { GenericResponse, TableRequestV2 } from 'services/types'
import { sendExportNotification, sendImportNotification } from 'utils'
import { orderApi } from './order'
import {
  AddAssetFinancialDetailRequest,
  AddFinancialDetailRequest,
  AssetFinancialDetailResponse,
  DeleteFinancialDetailRequest,
  EditAssetFinancialDetailRequest,
  EditFinancialDetailRequest,
  ExportRequest,
  FinancialDetailListRequest,
  FinancialDetailListResponse,
  FinancialDetailRequest,
  FinancialDetailResponse,
  MutationResponse,
  TableResponseWithTimezone,
  type BulkDeleteFinancialDetailRequest,
  type BulkDeleteFinancialDetailResponse,
} from './types'

export const financialDetailApi = createApi({
  reducerPath: 'financialDetailApi',
  tagTypes: [
    'FinancialDetails',
    'FinancialDetail',
    'FinancialDetailHistory',
    'AssetFinancialDetails',
    'AssetFinancialDetail',
    'AssetFinancialDetailHistory',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/engagement/'}`,
  }),
  endpoints: (builder) => ({
    getFinancialDetailList: builder.query<FinancialDetailListResponse, FinancialDetailListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/financial_detail/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['FinancialDetails'],
    }),
    addFinancialDetail: builder.mutation<MutationResponse, AddFinancialDetailRequest>({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/financial_detail/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['FinancialDetails', 'FinancialDetail'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(orderApi.util.invalidateTags(['OrderItems', 'OrderItemsTable']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editFinancialDetail: builder.mutation<
      MutationResponse & { service_point_id: string },
      EditFinancialDetailRequest
    >({
      query: ({ client_id, financial_detail_id, body }) => ({
        url: `clients/${client_id}/financial_detail/${financial_detail_id}/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['FinancialDetails', 'FinancialDetail', 'FinancialDetailHistory'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(orderApi.util.invalidateTags(['OrderItems', 'OrderItemsTable']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteFinancialDetail: builder.mutation<MutationResponse, DeleteFinancialDetailRequest>({
      query: ({ client_id, financial_detail_id }) => ({
        url: `clients/${client_id}/financial_detail/${financial_detail_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FinancialDetails'],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(orderApi.util.invalidateTags(['OrderItems', 'OrderItemsTable']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getFinancialDetail: builder.query<FinancialDetailResponse, FinancialDetailRequest>({
      query: ({ client_id, financial_detail_id }) => ({
        url: `clients/${client_id}/financial_detail/?financial_detail_id=${financial_detail_id}`,
        method: 'GET',
      }),
      providesTags: ['FinancialDetail'],
    }),
    exportFinancialDetailList: builder.mutation<MutationResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/financial_detail/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    importFinancialDetailList: builder.mutation<
      MutationResponse,
      { clientId: string; body: FormData }
    >({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/financial_detail/import/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['FinancialDetails'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (error) {}
      },
    }),
    financialDetailHistory: builder.query<
      TableResponseWithTimezone<{}>,
      TableRequestV2 & {
        financial_detail_id: string
      }
    >({
      query: ({ client_id, financial_detail_id, ...data }) => ({
        url: `clients/${client_id}/financial_detail/${financial_detail_id}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['FinancialDetailHistory'],
    }),
    getAssetFinancialDetailList: builder.query<
      FinancialDetailListResponse,
      FinancialDetailListRequest
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/asset_financial_detail/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['AssetFinancialDetails'],
    }),
    addAssetFinancialDetail: builder.mutation<MutationResponse, AddAssetFinancialDetailRequest>({
      query: ({ client_id, ...body }) => ({
        url: `clients/${client_id}/asset_financial_detail/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['AssetFinancialDetails', 'AssetFinancialDetail'],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(orderApi.util.invalidateTags(['OrderAssetTypePrices']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteAssetFinancialDetail: builder.mutation<MutationResponse, DeleteFinancialDetailRequest>({
      query: ({ client_id, financial_detail_id }) => ({
        url: `clients/${client_id}/asset_financial_detail/${financial_detail_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AssetFinancialDetails'],
      async onQueryStarted(_id, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(orderApi.util.invalidateTags(['OrderAssetTypePrices']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getAssetFinancialDetail: builder.query<
      AssetFinancialDetailResponse,
      { client_id: string; asset_financial_detail_id: string }
    >({
      query: ({ client_id, asset_financial_detail_id }) => ({
        url: `clients/${client_id}/asset_financial_detail/`,
        method: 'GET',
        params: {
          asset_financial_detail_id,
        },
      }),
      providesTags: ['AssetFinancialDetail'],
    }),
    editAssetFinancialDetail: builder.mutation<
      MutationResponse & { service_point_id: string },
      EditAssetFinancialDetailRequest
    >({
      query: ({ client_id, asset_financial_detail_id, body }) => ({
        url: `clients/${client_id}/asset_financial_detail/${asset_financial_detail_id}/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [
        'AssetFinancialDetails',
        'AssetFinancialDetail',
        'AssetFinancialDetailHistory',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(orderApi.util.invalidateTags(['OrderAssetTypePrices']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    assetFinancialDetailHistory: builder.query<
      TableResponseWithTimezone<{}>,
      TableRequestV2 & {
        asset_financial_detail_id: string
      }
    >({
      query: ({ client_id, asset_financial_detail_id, ...data }) => ({
        url: `clients/${client_id}/asset_financial_detail/${asset_financial_detail_id}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['AssetFinancialDetailHistory'],
    }),
    importAssetFinancialDetailList: builder.mutation<
      MutationResponse,
      { clientId: string; body: FormData }
    >({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/asset_financial_detail/import/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['AssetFinancialDetails'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (error) {}
      },
    }),
    exportAssetFinancialDetailList: builder.mutation<MutationResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/asset_financial_detail/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    bulkDeleteFinancialDetail: builder.mutation<
      BulkDeleteFinancialDetailResponse,
      BulkDeleteFinancialDetailRequest
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/pricing/bulk_delete/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['FinancialDetails', 'AssetFinancialDetails'],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // `onSuccess` side-effect
          dispatch(
            orderApi.util.invalidateTags([
              'OrderItems',
              'OrderItemsTable',
              'OrderAssetsTable',
              'OrderAssetTypePrices',
            ]),
          )
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    bulkPriceChangeFinancialDetail: builder.mutation<
      GenericResponse & {
        failure_list: Array<{
          id: string
          name: string
          type: 'entity_not_found' | 'service_point_not_found' | 'unknown'
        }>
        success_list: Array<{
          id: string
          name: string
        }>
      },
      {
        client_id: string
        model: 'financial_detail' | 'asset_financial_detail'
        id_list: Array<string>
        type: 'increase' | 'decrease'
        value_type: 'percentage' | 'flat_value'
        value: number
      }
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/pricing/bulk_price_change/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'FinancialDetails',
        'AssetFinancialDetails',
        'FinancialDetail',
        'AssetFinancialDetail',
        'FinancialDetailHistory',
        'AssetFinancialDetailHistory',
      ],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // `onSuccess` side-effect
          dispatch(
            orderApi.util.invalidateTags([
              'OrderItems',
              'OrderItemsTable',
              'OrderAssetsTable',
              'OrderAssetTypePrices',
            ]),
          )
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
  }),
})

export const {
  useGetFinancialDetailListQuery,
  useAddFinancialDetailMutation,
  useDeleteFinancialDetailMutation,
  useGetFinancialDetailQuery,
  useEditFinancialDetailMutation,
  useExportFinancialDetailListMutation,
  useImportFinancialDetailListMutation,
  useFinancialDetailHistoryQuery,
  useGetAssetFinancialDetailListQuery,
  useAddAssetFinancialDetailMutation,
  useDeleteAssetFinancialDetailMutation,
  useGetAssetFinancialDetailQuery,
  useEditAssetFinancialDetailMutation,
  useAssetFinancialDetailHistoryQuery,
  useImportAssetFinancialDetailListMutation,
  useExportAssetFinancialDetailListMutation,
  useBulkDeleteFinancialDetailMutation,
  useBulkPriceChangeFinancialDetailMutation,
} = financialDetailApi
